/* ═══════════════════════════════════════════════════════════════
   The Designer — the paragraph context menu.

   Two absolute rules of the design law meet here:
     · every feature is reachable from the visible UI — nothing is
       keyboard-only, because the user is an architect, not a developer
     · wherever the app can copy or paste, it offers copy AND cut AND
       paste, each reachable BOTH by menu and by shortcut

   So right-clicking a paragraph offers the full clipboard triad, the
   inline text controls Word users expect (bold / italic / underline /
   highlight), and the spec operations that Tab and Shift+Tab do from
   the keyboard.

   R5: "Level style…" lives here too. Level styling is a sub-tool, not a
   mode — this menu and the Levels palette are its two entry points, and
   clicking body text is neither of them.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import {
  deleteSelectedBlocks,
  duplicateSelectedBlocks,
  insertImageBlock,
  insertTableBlock,
  pickImageFile,
  setLevel,
  toggleExcluded,
} from './commands';

export interface BlockMenuState {
  x: number;
  y: number;
  level: number;
  excluded: boolean;
}

interface Props {
  editor: Editor;
  state: BlockMenuState;
  onClose: () => void;
  canEdit: boolean;
  /** Point the Levels palette at this paragraph's level and open it. */
  onLevelStyle?: (level: number) => void;
}

const MENU_W = 232;

/** The four character styles that exist. The forma owns everything else, so
 *  there is deliberately no font, size or colour path. */
const MARKS: Array<{ name: string; label: string; key: string }> = [
  { name: 'bold', label: 'Bold', key: 'Ctrl+B' },
  { name: 'italic', label: 'Italic', key: 'Ctrl+I' },
  { name: 'underline', label: 'Underline', key: 'Ctrl+U' },
  { name: 'highlight', label: 'Highlight', key: 'Ctrl+Alt+H' },
];
const LEVEL_NAMES = [
  'Level 1 — PART',
  'Level 2 — 1.01',
  'Level 3 — A.',
  'Level 4 — 1.',
  'Level 5 — a.',
  'Level 6 — 1)',
  'Level 7 — a)',
  'Level 8 — (1)',
  'Level 9 — (a)',
];

export function BlockMenu({ editor, state, onClose, canEdit, onLevelStyle }: Props) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [showLevels, setShowLevels] = React.useState(false);

  // Dismiss on mousedown OUTSIDE — never on hover-leave, and never on
  // mouseup, so a text-highlight drag that exits the menu survives.
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  // Focus the first item so the menu is usable without a mouse too.
  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  const copy = () => document.execCommand('copy');
  const cut = () => document.execCommand('cut');
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) editor.chain().focus().insertContent(text).run();
    } catch {
      // Clipboard permission denied — Ctrl+V still works, the browser
      // handles it natively without going through this path.
    }
  };

  // Clamp into the viewport so the menu is never clipped or off-screen.
  const maxH = Math.min(420, window.innerHeight - 16);
  const left = Math.min(state.x, window.innerWidth - MENU_W - 8);
  const top = Math.min(state.y, window.innerHeight - maxH - 8);

  return createPortal(
    <div
      ref={ref}
      className="dsg-menu"
      role="menu"
      aria-label="Paragraph actions"
      style={{ left, top, width: MENU_W, maxHeight: maxH }}
    >
      <button type="button" role="menuitem" className="dsg-menu-item" onClick={run(copy)}>
        Copy<span className="dsg-menu-key">Ctrl+C</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(cut)}
        disabled={!canEdit}
      >
        Cut<span className="dsg-menu-key">Ctrl+X</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(() => void paste())}
        disabled={!canEdit}
      >
        Paste<span className="dsg-menu-key">Ctrl+V</span>
      </button>

      <div className="dsg-menu-sep" role="separator" />

      {/* Inline text controls — direct formatting, additive over the level
          style, and a different thing entirely from revision marks. */}
      {MARKS.map((m) => (
        <button
          key={m.name}
          type="button"
          role="menuitemcheckbox"
          aria-checked={editor.isActive(m.name)}
          className={`dsg-menu-item${editor.isActive(m.name) ? ' is-current' : ''}`}
          onClick={run(() => editor.chain().focus().toggleMark(m.name).run())}
          disabled={!canEdit}
        >
          {m.label}
          <span className="dsg-menu-key">{m.key}</span>
        </button>
      ))}

      <div className="dsg-menu-sep" role="separator" />

      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(() =>
          duplicateSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view))
        )}
        disabled={!canEdit}
        title="Insert a copy of this paragraph right below it"
      >
        Duplicate<span className="dsg-menu-key">Ctrl+D</span>
      </button>
      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(() =>
          deleteSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view))
        )}
        disabled={!canEdit}
        title="Delete this paragraph — Ctrl+Z brings it back"
      >
        Delete
      </button>

      <div className="dsg-menu-sep" role="separator" />

      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(() => {
          // The picker outlives the menu; the insert lands when it resolves.
          void pickImageFile().then((img) => {
            if (img) {
              insertImageBlock(
                editor.state,
                editor.view.dispatch.bind(editor.view),
                img.dataUrl,
                img.name
              );
            }
          });
        })}
        disabled={!canEdit}
        title="Insert an image below this paragraph, at print width"
      >
        Insert image
      </button>
      <button
        type="button"
        role="menuitem"
        className="dsg-menu-item"
        onClick={run(() => insertTableBlock(editor.state, editor.view.dispatch.bind(editor.view)))}
        disabled={!canEdit}
        title="Insert a 3×3 table below this paragraph"
      >
        Insert table
      </button>

      <div className="dsg-menu-sep" role="separator" />

      <button
        type="button"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={showLevels}
        className="dsg-menu-item"
        onClick={() => setShowLevels((v) => !v)}
        disabled={!canEdit}
      >
        Change level<span className="dsg-menu-key">Tab</span>
      </button>
      {showLevels && (
        <div className="dsg-menu-sub" role="group" aria-label="Levels">
          {LEVEL_NAMES.map((name, i) => (
            <button
              key={i}
              type="button"
              role="menuitemradio"
              aria-checked={state.level === i + 1}
              className={`dsg-menu-item${state.level === i + 1 ? ' is-current' : ''}`}
              onClick={run(() =>
                setLevel(editor.state, editor.view.dispatch.bind(editor.view), i + 1)
              )}
            >
              {name}
              {state.level === i + 1 && <span className="dsg-menu-key">Current</span>}
            </button>
          ))}
        </div>
      )}

      {onLevelStyle && (
        <button
          type="button"
          role="menuitem"
          className="dsg-menu-item"
          onClick={run(() => onLevelStyle(state.level))}
          disabled={!canEdit}
          title={`Open the Levels palette on level ${state.level} — restyles every level ${state.level} paragraph`}
        >
          Level style…
          <span className="dsg-menu-key">L{state.level}</span>
        </button>
      )}

      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={state.excluded}
        className="dsg-menu-item"
        onClick={run(() => toggleExcluded(editor.state, editor.view.dispatch.bind(editor.view)))}
        disabled={!canEdit}
        title="Keep the paragraph here but leave it out of the exported document"
      >
        {state.excluded ? 'Include in export' : 'Skip at export'}
      </button>
    </div>,
    document.body
  );
}
